
import { Language, translations } from './i18n/translations';

export type TranslationFunction = (key: keyof typeof translations.en, options?: Record<string, string | number>) => string;

export interface UboDetail {
  id: string; // For React key
  uboName: string;
  uboTrnTaxId: string;
  uboNationality: string;
  uboCountryOfResidence: string;
}

export interface ContactPersonDetail {
  contactName: string;
  contactEmail: string;
  contactMobile: string;
  contactLandline?: string;
}

export interface AddressDetail {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  emirateOrState: string;
  poBox?: string;
  country: string;
}

export interface BusinessActivityItem {
  id: string;
  activity: string;
}
export interface RevenuePercentageItem {
  id: string;
  sector: string;
  percentage: string;
}
export interface UnincorporatedPartnershipItem {
    id: string;
    name: string;
    trn: string;
}

export interface NaturalPersonSpecifics {
  uaeResidencyStatus?: 'uae_resident' | 'non_uae_resident' | ''; // Derived from 4.1.9
  nationality?: string;
  turnover?: string; // 4.1.3
  accountingBasis?: 'cash' | 'accrual' | ''; // 4.1.4
  conductsMultipleBusinesses?: 'yes' | 'no' | ''; // 4.1.5
  businessActivitiesCount?: string; // 4.1.6
  businessActivityList?: BusinessActivityItem[]; // 4.1.7
  revenuePercentageBySector?: RevenuePercentageItem[]; // 4.1.8
  taxResidencyStatusDTA?: 'resident' | 'non-resident' | 'na' | '';  // 4.1.9 (Wording adjusted to reflect DTA context if non-UAE resident for CT)
  nonResidentCountryDTA?: string; // 4.1.10
  isPartnerInUnincorporatedPartnerships?: 'yes' | 'no' | ''; // 4.1.1
  unincorporatedPartnerships?: UnincorporatedPartnershipItem[]; // 4.1.2
}

export interface JuridicalPersonSpecifics {
  incorporationDate?: string;
  incorporationCountry?: string; // Pre-populated, relevant if not UAE inc (Ref 4.2.19)
  isUaeOrFzIncorporated?: 'yes' | 'no' | ''; // 4.2.12
  isTaxResidentInForeignJurisdictionUnderDTA?: 'yes' | 'no' | ''; // 4.2.13 & 4.2.17
  dtaForeignJurisdictionCountry?: string; // 4.2.14 & 4.2.18
  isNonResidentInUaeUnderDTA?: 'yes' | 'no' | ''; // 4.2.15

  isForeignIncorporatedUaeControlled?: 'yes' | 'no' | ''; // 4.2.16
  nonResidentFilingBasis?: 'permanent_establishment' | 'uae_nexus' | ''; // 4.2.20
  isBankOrInsuranceProvider?: 'yes' | 'no' | ''; // Used in Step 2 logic

  isMneGroupMember?: 'yes' | 'no' | ''; // 4.2.1
  revenueForTaxPeriod?: string; // 4.2.6
  accountingBasis?: 'cash' | 'accrual' | ''; // 4.2.7
  conductsMultipleBusinesses?: 'yes' | 'no' | ''; // 4.2.8
  businessActivitiesCount?: string; // 4.2.9
  businessActivityList?: BusinessActivityItem[]; // 4.2.10
  revenuePercentageBySector?: RevenuePercentageItem[]; // 4.2.11

  isPartnerInUnincorporatedPartnerships?: 'yes' | 'no' | ''; // 4.2.2
  unincorporatedPartnerships?: UnincorporatedPartnershipItem[]; // 4.2.3

  isGovernmentEntityOrAffiliate?: 'yes' | 'no' | ''; // 4.2.4
  conductsTaxableActivityIfGovt?: 'yes' | 'no' | ''; // 4.2.5

  isFreeZonePerson?: 'yes' | 'no' | ''; // 4.3.1 (also applies if filingAs Juridical)
  electionToForego0RateFZ?: 'yes' | 'no' | ''; // 4.3.2
  meetsQualifyingFZRequirements?: 'yes' | 'no' | ''; // 4.3.3
}

export interface UnincorporatedPartnershipSpecifics { // Fields from CTGTXR1 4.4
  isMneGroupMember?: 'yes' | 'no' | ''; // 4.4.1
  isPartnerInOtherUPs?: 'yes' | 'no' | ''; // 4.4.2
  otherUPs?: UnincorporatedPartnershipItem[]; // 4.4.3 (List of TRNs)
  revenueForTaxPeriod?: string; // 4.4.4
  accountingBasis?: 'cash' | 'accrual' | ''; // 4.4.5
  conductsMultipleBusinesses?: 'yes' | 'no' | ''; // 4.4.6
  businessActivitiesCount?: string; // 4.4.7
  businessActivityList?: BusinessActivityItem[]; // 4.4.8
  revenuePercentageBySector?: RevenuePercentageItem[]; // 4.4.9
}

export interface TaxGroupMemberItem {
  id: string;
  trn: string;
  name: string;
}
export interface TaxGroupSpecifics { // Fields from CTGTXR1 4.5
  taxGroupName?: string;
  parentCompanyTrn?: string;
  members?: TaxGroupMemberItem[];
  isMneGroupMember?: 'yes' | 'no' | ''; // 4.5.1
  isPartnerInUnincorporatedPartnerships?: 'yes' | 'no' | ''; // 4.5.2 (for any member)
  unincorporatedPartnerships?: UnincorporatedPartnershipItem[]; // 4.5.3 (List of TRNs)
  consolidatedRevenue?: string; // 4.5.4
  accountingBasis?: 'cash' | 'accrual' | ''; // 4.5.5
  conductsMultipleBusinesses?: 'yes' | 'no' | ''; // 4.5.6
  businessActivitiesCount?: string; // 4.5.7
  businessActivityList?: BusinessActivityItem[]; // 4.5.8
  revenuePercentageBySector?: RevenuePercentageItem[]; // 4.5.9
}

export interface TaxablePersonDetails {
  filingAs?: 'natural_person' | 'juridical_person' | 'unincorporated_partnership' | 'tax_group_parent' | 'exempt_person' | 'free_zone_person' | '';
  isExemptPerson?: 'yes' | 'no' | ''; // Common question before branching
  exemptPersonHasTaxableBusiness?: 'yes' | 'no' | ''; // If isExemptPerson is 'yes'
  taxablePersonName: string; // Pre-populated
  trn: string; // Pre-populated
  primaryBusinessActivity: string;
  mainAddress: AddressDetail; // Pre-populated
  taxPeriodStartDate: string; // Pre-populated
  taxPeriodEndDate: string; // Pre-populated
  contactPerson: ContactPersonDetail;
  isUboInfoDisclosed?: 'yes' | 'no' | '';
  uboDetails: UboDetail[];

  naturalPersonSpecifics: NaturalPersonSpecifics;
  juridicalPersonSpecifics: JuridicalPersonSpecifics;
  unincorporatedPartnershipSpecifics: UnincorporatedPartnershipSpecifics;
  taxGroupSpecifics: TaxGroupSpecifics;
}

export interface Elections {
  realisationBasis: boolean; // 5.1.1
  realisationBasisBankOption?: 'A' | 'B' | ''; // 5.1.3
  transitionalRulesImmovableProperty: boolean; // 5.2.1
  transitionalRulesIntangibleAssets: boolean; // 5.2.2
  transitionalRulesFinancialAssetsLiabilities: boolean; // 5.2.3
  smallBusinessRelief: boolean; // 5.3.2
  transfersWithinQualifyingGroup: boolean; // 5.4.2
  hasTransfersWithinQGInPeriod?: boolean; // 5.4.1 (Is actually a question, not an election itself)
  businessRestructuringRelief: boolean; // 5.5.2
  hasBusinessRestructuringInPeriod?: boolean; // 5.5.1 (Is actually a question)
  hasForeignPEs?: boolean; // 5.6.1 (Is actually a question)
  foreignPermanentEstablishmentExemption: boolean; // 5.6.2
}

export interface AccountingSchedule { // Corresponds to CTGTXR1 Section 6
  accountingIncome: string; // 7.1.1 (This is Net Profit/Loss from Income Statement)
  financialStatementsAudited: string; // 'yes' | 'no' | '' (6.2.1)
  auditOpinion?: 'qualified' | 'unqualified' | ''; // 6.2.2
  auditorName?: string; // 6.2.3
  // Income Statement Details (Ref: CTGTXR1 pg 53)
  incomeStatement_revenue: string;
  incomeStatement_costOfSales: string;
  incomeStatement_grossProfit: string;
  incomeStatement_operatingExpenses: string;
  incomeStatement_otherIncome: string; // Net Other Income/(Expenses)
  // OCI (Ref: CTGTXR1 pg 53)
  oci_itemsNetOfTax: string;
  // Balance Sheet (Statement of Financial Position) (Ref: CTGTXR1 pg 53)
  balanceSheet_totalAssets: string;
  balanceSheet_totalLiabilities: string;
  balanceSheet_totalEquity: string;
}

export interface AccountingAdjustments { // Corresponds to CTGTXR1 Section 7
  equityMethodInvestmentAccounting: string; // 'yes' | 'no' | '' (7.2.1)
  equityMethodShareOfProfits: string; // 7.2.2 (Deduct)
  equityMethodShareOfLosses: string; // 7.2.3 (Add)
  unincorporatedPartnershipSeparateTP: string; // 'yes' | 'no' | '' (7.3.1)
  unincorporatedPartnershipNetProfits: string; // 7.3.2 (Deduct)
  unincorporatedPartnershipNetLosses: string; // 7.3.3 (Add)
  upInterestDisposalGainsPE?: string; // 7.3.4 (Add)
  upInterestDisposalLossesPE?: string; // 7.3.5 (Deduct)
  incomeLossesNotInISRecognised: string; // 'yes' | 'no' | '' (7.4.1)
  gainsNotInIS: string; // 7.4.2 (From Sch 22.1 - Add)
  lossesNotInIS: string; // 7.4.3 (From Sch 22.1 - Deduct)
  unrealisedGainsRecognisedCurrentTaxPeriod: string; // 7.5.2 (From Sch 22.2 - Deduct)
  unrealisedLossesRecognisedCurrentTaxPeriod: string; // 7.5.3 (From Sch 22.2 - Add)
  deferredGainsRealisedCurrentTaxPeriod?: string; // 7.5.5 (From Sch 22.3 - Add)
  deferredLossesRealisedCurrentTaxPeriod?: string; // 7.5.6 (From Sch 22.3 - Deduct)
  depreciationAdjustmentsRealisationDecrease?: string; // 7.5.7 (Deduct)
  depreciationAdjustmentsRealisationIncrease?: string; // 7.5.8 (Add)
  otherRealisationAdjustmentsDecreaseTI?: string; // 7.5.9 (Deduct)
  otherRealisationAdjustmentsIncreaseTI?: string; // 7.5.10 (Add)
  otherRealisationAdjustmentsDesc?: string; // 7.5.11
  gainsOnElectedImmovableProperty: string; // 7.6.3 (From Sch 21.A - Deduct)
  transitionalRulesIntangibleAssetsAdjustment: string; // 7.6.5 (From Sch 21.B - Deduct/Add)
  transitionalRulesFinancialAssetsLiabilitiesAdjustment: string; // Net from Sch 21.C/D (Deduct/Add)
  dividendsFromUaeResident: string; // 'yes' | 'no' | '' (7.7.1)
  totalDividendsFromUaeResident: string; // 7.7.2 (From Sch 13 - Deduct)
  incomeLossesFromParticipatingInterest: string; // 'yes' | 'no' | '' (7.7.3)
  incomeFromParticipatingInterestExempted: string; // 7.7.4 (From Sch 18 - Deduct)
  incomeFromParticipatingInterestClawback?: string; // 7.7.5 (Add)
  lossesFromParticipatingInterestClawback?: string; // 7.7.6 (Deduct)
  taxableIncomeLossFromEligibleForeignPE: string; // 7.8.1 (From Sch 14 - Deduct if Income, Add if Loss for offset)
  incomeFromInternationalAircraftShippingNonResident: string; // 'yes' | 'no' | '' (7.9.1)
  countryInternationalAircraftShipping: string; // 7.9.2
  incomeDerivedIntlAircraftShipping: string; // 7.9.3 (Deduct)
  expenditureIntlAircraftShipping: string; // 7.9.4 (Add)
}

export interface ReliefApplications { // Corresponds to CTGTXR1 Section 8
  transfersWithinQualifyingGroupAdjustmentNeeded: string; // 'yes' | 'no' | '' (8.1.1)
  gainsLossesSubjectToTgReliefCurrentPeriod: string; // 8.1.4 (From Sch 20.A - Deduct Gain, Add Loss)
  gainsLossesFromTgClawback: string; // 8.1.5 (From Sch 20.A - Add Gain, Deduct Loss)
  qgExcludedUnrealisedGainsTransferee?: string; // 8.1.6
  qgExcludedUnrealisedLossesTransferee?: string; // 8.1.7
  qgRealisedGainsPreviouslyExcluded?: string; // 8.1.8
  qgRealisedLossesPreviouslyExcluded?: string; // 8.1.9
  qgDepreciationAdjustmentDecreaseTransferee?: string; // 8.1.10
  qgDepreciationAdjustmentIncreaseTransferee?: string; // 8.1.11
  qgOtherAdjustmentsClawbackIncreaseTI?: string; // 8.1.12
  qgOtherAdjustmentsClawbackDecreaseTI?: string; // 8.1.13
  qgOtherAdjustmentsDesc?: string; // 8.1.14
  totalGainLossNotTaxedQGCurrentPeriod?: string; // Helper sum from Sch 20.A
  totalGainLossRecognisedClawbackQG?: string; // Helper sum from Sch 20.A
  businessRestructuringReliefAdjustmentNeeded: string; // 'yes' | 'no' | '' (8.2.1)
  gainsLossesSubjectToBrrReliefCurrentPeriod: string; // 8.2.4 (From Sch 20.B - Deduct Gain, Add Loss)
  gainsLossesFromBrrClawback: string; // 8.2.5 (From Sch 20.B - Add Gain, Deduct Loss)
  brrExcludedUnrealisedGainsTransferee?: string; // 8.2.6
  brrExcludedUnrealisedLossesTransferee?: string; // 8.2.7
  brrRealisedGainsPreviouslyExcluded?: string; // 8.2.8
  brrRealisedLossesPreviouslyExcluded?: string; // 8.2.9
  brrDepreciationAdjustmentDecreaseTransferee?: string; // 8.2.10
  brrDepreciationAdjustmentIncreaseTransferee?: string; // 8.2.11
  brrOtherAdjustmentsClawbackIncreaseTI?: string; // 8.2.12
  brrOtherAdjustmentsClawbackDecreaseTI?: string; // 8.2.13
  brrOtherAdjustmentsDesc?: string; // 8.2.14
  totalGainLossNotTaxedBRRCurrentPeriod?: string; // Helper sum from Sch 20.B
  totalGainLossRecognisedClawbackBRR?: string; // Helper sum from Sch 20.B
}

export interface OtherAdjustments { // Corresponds to CTGTXR1 Section 9
  nonDeductibleEntertainmentExp: string; // 9.1.1 (Add)
  nonDeductiblePensionContributions: string; // 9.1.2 (Add)
  nonDeductibleCharitableDonationsNonQPBE: string; // 9.1.3 (Add)
  expenditureDerivingExemptIncomeNonInterest: string; // 9.1.4 (Add)
  ownerDividendsExpensed: string; // 9.1.5 (Add)
  naturalPersonWithdrawals: string; // 9.1.6 (Add - only for Natural Persons)
  expensesNotWhollyExclusivelyBusiness: string; // 9.1.7 (Add)
  otherNonDeductibleExpenditure: string; // 9.1.8 (Add)
  otherNonDeductibleExpenditureDesc: string; // 9.1.9
  netInterestExpExceedsThreshold: string; // 'yes' | 'no' | '' (9.2.1)
  disallowedNetInterestExpCurrentPeriod: string; // 9.2.3 (From Sch 19 - Add)
  broughtForwardNieUtilised: string; // 9.2.4 (From Sch 19 - Deduct)
  preGroupingNIETaxGroups?: string; // 9.2.5 (Tax Groups Only, from Sch 19 - Deduct)
  specificInterestNonDeductible: string; // 9.2.6 (Add)
  relatedPartyTransactionsExist: string; // 'yes' | 'no' | '' (9.3.1)
  relatedPartyTransactionsAggregateValueExceeds40M?: 'yes' | 'no' | ''; // 9.3.2
  additionsArmslengthAdjustments: string; // 9.3.3 (From Sch 16.1 - Add)
  deductionsArmslengthAdjustments?: string; // 9.3.4 (From Sch 16.1 - Deduct)
  relatedPartyAssetGainLossRealised: string; // 'yes' | 'no' | '' (9.3.5)
  relatedPartyAssetGain: string; // 9.3.6 (Add)
  relatedPartyAssetLoss: string; // 9.3.7 (Deduct)
  connectedPersonsTransactionsExist: string; // 'yes' | 'no' | '' (9.3.8)
  connectedPersonsPaymentsAggregateValueExceeds500K?: 'yes' | 'no' | ''; // 9.3.9
  adjustmentsConnectedPersonsNonMarketValue: string; // 9.3.10 (From Sch 16.2 - Add)
  investorInQIF: string; // 'yes' | 'no' | '' (9.4.1)
  qifNetInterestIncome: string; // 9.4.2 (Deduct)
  qifNetRentalIncome?: string; // 9.4.3 (Deduct)
  qifNetExemptIncome?: string; // 9.4.4 (Deduct)
  qifOtherIncome?: string; // 9.4.5 (Deduct)
  qifDividendsDeclaredByTPFromQIF?: string; // 9.4.6 (Add)
  priorPeriodErrorCorrectionImpactAED10000OrLess: string; // 'yes' | 'no' | '' (9.5.1)
  priorPeriodErrorAdjustmentsTaxPeriods?: string[]; // 9.5.2
  priorPeriodErrorAdjustmentsIncreaseTI: string; // 9.5.3 (Add)
  priorPeriodErrorAdjustmentsDecreaseTI?: string; // To balance 9.5.3
  priorPeriodErrorAdjustmentsDesc?: string; // For description related to 9.5.1-9.5.3
  otherAdjustmentsNotCapturedAbove?: 'yes' | 'no' | ''; // 9.5.5
  otherAdjustmentsNotCapturedIncreaseTI?: string; // 9.5.6 (Add)
  otherAdjustmentsNotCapturedDecreaseTI?: string; // 9.5.7 (Deduct)
  otherAdjustmentsNotCapturedDesc?: string; // 9.5.8
}

export interface TaxLiability { // Corresponds to CTGTXR1 Section 10
  taxableIncomeLossBeforeTaxLossUtilisation: string; // 10.1.1 (Calculated)
  hasBroughtForwardTaxLosses: string; // 'yes' | 'no' | '' (10.2.1 for non-group / 10.3.1 for group)
  claimOrSurrenderTaxLossesGroupEntity?: 'yes' | 'no' | ''; // 10.2.2 for non-group / 10.3.2 for group
  taxLossesUtilisedCurrentPeriod: string; // From Sch 17 (10.2.3 for non-group / 10.3.3 for group)
  taxLossesClaimedFromOtherGroupEntities?: string; // 10.2.4 (non-group, from Sch 17.A)
  preGroupingTaxLossesUtilisedTaxGroup?: string; // 10.3.4 (group, from Sch 17.B)
  taxLossesClaimedFromOtherGroupEntitiesTaxGroup?: string; // 10.3.5 (group, from Sch 17.B)
  taxableIncomeAfterTaxLossAdjustments: string; // 10.4.1 (Calculated)
  corporateTaxLiabilityBeforeCredits: string; // 10.4.2 (Calculated)
  wishToUseTaxCredits: string; // 'yes' | 'no' | '' (10.4.3)
  taxCreditsApplied: string; // 10.4.4 (From Sch 15)
  corporateTaxPayable: string; // 10.4.5 (Calculated)
  estimatedFiguresIncludedInReturn: string; // 'yes' | 'no' | '' (10.5.1)
  estimatedFiguresDetails: string; // 10.5.2
}

// Definitions for Schedules (CTGTXR1 Sections 12-22)
export interface EmirateBreakdown { // For FZ Schedule 12.1.39
  id: string;
  emirate: 'abu_dhabi' | 'dubai' | 'sharjah' | 'ajman' | 'umm_al_quwain' | 'ras_al_khaimah' | 'fujairah' | '';
  ebitda: string;
  salariesAndWages: string;
}
export interface FreeZoneOutsourcingProvider { // For FZ Schedule 12.1.26-32
  id: string;
  name: string; // 12.1.27
  address: string; // 12.1.28
  trn: string; // 12.1.29
  expenditure: string; // 12.1.30
  avgEmployees: string; // 12.1.31
  adequateSupervision: boolean; // 12.1.32
}
export interface FreeZoneQualifyingActivities { // For FZ Schedule 12.1.18
  manufacturing: boolean;
  processing: boolean;
  holdingShares: boolean;
  shipManagement: boolean;
  reinsurance: boolean;
  fundManagement: boolean;
  wealthManagement: boolean;
  headquarters: boolean;
  treasury: boolean;
  aircraftFinancing: boolean;
  distributionDesignatedZone: boolean;
  logistics: boolean;
  ancillaryActivities: boolean;
}
export interface FreeZoneScheduleData { // Corresponds to CTGTXR1 Section 12.1
  totalRevenueFinancialStatements: string; // 12.1.1
  hasDomesticOrForeignPE: string; // 'yes' | 'no' | '' (12.1.2)
  revenueAttrToPE: string; // 12.1.3
  hasRevenueFromNonFzCommercialPropertyInFz: string; // 'yes' | 'no' | '' (12.1.4)
  revenueNonFzCommercialPropertyInFz: string; // 12.1.5
  hasRevenueFromNonCommercialPropertyInFz: string; // 'yes' | 'no' | '' (12.1.6)
  revenueNonCommercialPropertyInFz: string; // 12.1.7
  hasRevenueFromIp: string; // 'yes' | 'no' | '' (12.1.8)
  revenueIpNonQualifying: string; // 12.1.9 (From Sch 12.2)
  totalRevenueForDeMinimis?: string; // 12.1.10 (Calculated)
  revenueExcludedActivities: string; // 12.1.11
  revenueNonQualifyingActivitiesNonFzp: string; // 12.1.12
  revenueTransactionsNonBeneficialRecipient: string; // 12.1.13
  totalNonQualifyingRevenue?: string; // 12.1.14 (Calculated)
  deMinimisMetResult?: boolean; // 12.1.15 (Calculated)
  revenueFromFzpQualifying: string; // 12.1.16
  revenueFromNonFzpQualifyingActivities: string; // 12.1.17
  qualifyingActivities: FreeZoneQualifyingActivities; // 12.1.18
  revenueIpQualifying: string; // 12.1.19 (From Sch 12.2)
  allEmployeesInFz: string; // 'yes' | 'no' | '' (12.1.20)
  avgNumFullTimeEmployees: string; // 12.1.21
  avgNumFullTimeEmployeesInFz: string; // 12.1.22
  totalOperatingExpenditureQI: string; // 12.1.23
  totalCapitalExpenditureQI: string; // 12.1.24
  coreIncomeActivitiesOutsourced: string; // 'yes' | 'no' | '' (12.1.25)
  numOutsourcingProviders: string; // 12.1.26
  outsourcingProviders: FreeZoneOutsourcingProvider[]; // For 12.1.27 - 12.1.32
  adequateAssetsInFz: boolean; // 12.1.33
  adequateEmployeesInFz: boolean; // 12.1.34
  adequateOperatingExpenditureInFz: boolean; // 12.1.35
  relatedPartyTransactionsArmslength: boolean; // 12.1.36
  transferPricingDocumentationPrepared: boolean; // 12.1.37
  auditedFinancialStatementsPrepared: boolean; // 12.1.38
  emirateBreakdown: EmirateBreakdown[]; // 12.1.39
  additionalInformation: string; // 12.1.40
  calculatedQualifyingIncome?: string;
  calculatedNonQualifyingIncome?: string;
}

export interface Schedule12IPItem { // Corresponds to CTGTXR1 Section 12.2
  id: string;
  description: string; // 12.2.1
  overallIncome: string; // 12.2.2
  overallExpenditure: string; // 12.2.3
  qualifyingExpenditure: string; // 12.2.4
  upliftExpenditure?: string; // 12.2.5 (Calculated)
  revenueAttrToQI?: string; // 12.2.6 (Calculated)
  incomeInExcessOfQI?: string; // 12.2.7 (Calculated)
}
export interface Schedule12IPData {
  ipItems: Schedule12IPItem[];
  totalIncomeInExcessOfQI?: string; // 12.2.8 (Sum of 12.2.7 for all items)
  totalIncomeFromOtherIPNonQualifying: string; // 12.2.9
  totalRevenueFromIPNotQualifyingIncome?: string; // 12.2.10 (Calculated: 12.2.8 + 12.2.9) -> to FZ Sch 12.1.9
  totalRevenueFromQualifyingIP?: string; // (Sum of 12.2.6 for all items) -> to FZ Sch 12.1.19
}

export interface UaeDividendItem {
  id: string;
  companyName: string;
  trn: string;
  amount: string;
}
export interface Schedule13UaeDividendsData {
  items: UaeDividendItem[];
  totalDividendsReceived: string;
}

export interface ForeignPEPriorPeriodItem {
  id: string;
  taxPeriodIdentifier: string; // 14.1.1
  taxableIncomeFPE: string; // 14.1.2
  taxLossFPE: string; // 14.1.3
  electionMadeInPeriod: 'yes' | 'no' | ''; // 14.1.4
  lossesUtilisedAgainstMainTI: string; // 14.1.5
  lossesUtilisedAgainstFPEIncome: string; // 14.1.6
  cfLossesUtilisedAgainstFPEIncome?: string; // 14.1.7 (Calculated)
}
export interface IneligiblePEItem {
  id: string;
  jurisdictionName: string; // 14.3
}
export interface Schedule14ForeignPEData {
  priorPeriods: ForeignPEPriorPeriodItem[];
  currentFPEligibleNetIncome: string; // 14.2.2
  currentFPEligibleNetLoss: string; // 14.2.3
  ineligiblePEs: IneligiblePEItem[];
  netIncomeLossForAdjustment: string; // Calculated for Step 4
  priorLossesFullyOffset: boolean; // Calculated helper
}

export interface ForeignTaxCreditItem {
  id: string;
  country: string; // 15.1.1
  incomeSubjectToForeignTax: string; // 15.1.2
  foreignTaxPaid: string; // 15.1.3
  evidenceConfirmation: 'yes' | 'no' | ''; // 15.1.4
  natureOfCredit: string; // 15.1.5
  uaeTaxOnRelevantIncome?: string; // 15.1.6 (Calculated)
  taxCreditClaimed?: string; // 15.1.7 (Calculated)
}
export interface ForeignTaxCreditTaxGroupMemberItem extends ForeignTaxCreditItem {
  memberName: string; // 15.2.1
  taxableIncomeAttributableToMember: string; // 15.2.2
  // Foreign Tax Paid inherited but refers to member's payment (15.2.3)
  // UAE Tax on Relevant Income is member's UAE tax (15.2.4)
  // Tax Credit Claimed is for member (15.2.5)
}
export interface Schedule15TaxCreditData {
  foreignTaxCredits: ForeignTaxCreditItem[];
  foreignTaxCreditsForGroupMembers: ForeignTaxCreditTaxGroupMemberItem[];
  totalTaxCreditsClaimed: string; // Calculated
}

export interface RelatedPartyTransactionItem {
    id: string;
    name: string; // 16.1.1
    transactionType: 'goods' | 'services' | 'intellectual_property' | 'interest' | 'assets' | 'liabilities' | 'other' | ''; // 16.1.2
    taxResidence: string; // 16.1.3
    trnOrTin: string; // 16.1.4
    grossIncomeOrExpense: string; // 16.1.5
    transferPricingMethod: 'cup' | 'resale_price' | 'cost_plus' | 'tnmm' | 'profit_split' | 'other' | ''; // 16.1.6
    otherMethodDescription?: string; // 16.1.7
    armsLengthValue: string; // 16.1.8
    taxAdjustment?: string; // 16.1.9 (Calculated)
}
export interface Schedule16RelatedPartyData {
    transactions: RelatedPartyTransactionItem[];
    totalUpwardAdjustments: string; // Calculated
    totalDownwardAdjustments: string; // Calculated
}

export interface ConnectedPersonPaymentItem {
    id: string;
    name: string; // 16.2.1
    trnOrTin: string; // 16.2.2
    paymentOrBenefitType: 'remuneration' | 'interest' | 'royalties' | 'other_services' | 'other_in_kind' | 'other' | ''; // 16.2.3
    description: string; // 16.2.4
    valueProvidedByTP: string; // 16.2.5
    marketValue: string; // 16.2.6
    adjustment?: string; // 16.2.7 (Calculated)
}
export interface Schedule16ConnectedPersonsData {
    payments: ConnectedPersonPaymentItem[];
    totalAdjustments: string; // Calculated
}

export interface TaxLossItem {
  id: string;
  description: string; // For B/F losses
  amount: string;
}
export interface TaxLossTransferItem {
  id: string;
  entityName: string;
  trn: string;
  amount: string;
}
export interface Schedule17TaxLossData { // For individuals/non-groups
  broughtForwardLosses: TaxLossItem[]; // 17.1.1
  taxLossIncurredCurrentPeriod: string; // 17.1.2 (Calculated from main form)
  taxLossesReceivedBizRestructuring: TaxLossItem[]; // 17.1.3
  taxLossesTransferredBizRestructuring: string; // 17.1.4
  taxLossesLimitedChangeOwnership: string; // 17.1.5
  otherAdjustmentsIncrease: string; // 17.1.6
  otherAdjustmentsDecrease: string; // 17.1.7
  otherAdjustmentsDesc: string; // 17.1.8
  // 17.1.9 is Tax Losses Utilised, taken from calculated field
  taxLossesReceivedFromOthers: TaxLossTransferItem[]; // 17.2.x
  taxLossesTransferredToOthers: TaxLossTransferItem[]; // 17.3.x
  taxLossesUtilisedInCurrentPeriodFromSchedule: string; // Calculated within schedule
  taxLossesCarriedForwardAvailableForTransfer: string; // Calculated within schedule (17.1.10 related)
  taxLossesCarriedForwardFinal: string; // Calculated within schedule (17.3.5 related)
}

export interface TaxGroupNewSubsidiaryItem {
  id: string;
  name: string; // 17.5.1
  trn: string; // 17.5.2
  preGroupingLossBroughtIn: string; // 17.5.3
  taxableIncomeAttributable: string; // 17.5.4
}
export interface TaxGroupPreGroupingLossItem {
  id: string;
  subsidiaryName: string; // 17.6.1
  subsidiaryTrn: string; // 17.6.2
  preGroupingLossBroughtForward: string; // 17.6.3
  taxableIncomeAttributable: string; // 17.6.5
  lossAttributable?: string; // 17.6.6
  preGroupingLossUtilised?: string; // 17.6.7 (Calculated)
  // 17.6.8, 17.6.9 (adjustments), 17.6.10 (desc), 17.6.11 (c/f)
  otherAdjustmentsIncrease?: string;
  otherAdjustmentsDecrease?: string;
  otherAdjustmentsDesc?: string;
  preGroupingLossCarriedForward?: string;
}
export interface Schedule17TaxGroupLossData {
  newSubsidiaries: TaxGroupNewSubsidiaryItem[];
  preGroupingLossItems: TaxGroupPreGroupingLossItem[];
  taxGroupLossesBroughtForward: string; // 17.7.1
  taxGroupLossIncurredCurrentPeriod: string; // 17.7.2 (Calculated from main form)
  taxGroupLossesReceivedBizRestructuring: string; // 17.7.3
  taxGroupLossesTransferredBizRestructuring: string; // 17.7.4
  taxGroupLossesLimitedChangeOwnership: string; // 17.7.5
  // 17.7.6, 17.7.7, 17.7.8 are other adjustments for group losses
  taxGroupOtherAdjustmentsIncrease: string;
  taxGroupOtherAdjustmentsDecrease: string;
  taxGroupOtherAdjustmentsDesc: string;
  // 17.7.9 is Utilised losses for group
  taxLossesClaimedFromOthers: TaxLossTransferItem[]; // 17.8.x
  taxLossesTransferredToOthers: TaxLossTransferItem[]; // 17.9.x
  // Calculated fields
  taxGroupLossesUtilisedInCurrentPeriod: string; // Summary field
  taxGroupLossesCarriedForwardAvailableForTransfer: string; // (17.7.10 related)
  finalTaxGroupLossesCarriedForward: string; // (17.9.5 related)
}

export interface ParticipationIncomeLossItem {
  id: string;
  natureOfIncomeLoss: 'foreign_dividends' | 'capital_gains_losses' | 'fx_gains_losses' | 'impairment_gains_losses' | ''; // 18.6
  incomeToExempt: string; // 18.7
  lossToExempt: string; // 18.8
}
export interface ParticipationExemptionItem {
  id: string;
  name: string; // 18.1
  countryOfResidency: string; // 18.2
  trnOrTin: string; // 18.3
  subjectToTaxConfirmation: 'yes' | 'no' | ''; // 18.4
  subjectToTaxReason?: string; // 18.5 (if 18.4 is 'No')
  incomeLossItems: ParticipationIncomeLossItem[];
  ownershipPercentage: string; // 18.9
  acquisitionCost: string; // 18.10
  dateIncomeLossDerived: string; // 18.11
  heldFor12MonthsConfirmation: 'yes' | 'no' | ''; // 18.12
  intendToHoldFor12MonthsConfirmation?: 'yes' | 'no' | ''; // 18.13 (if 18.12 is 'No')
  otherConditionsMetConfirmation: 'yes' | 'no' | ''; // 18.14
  // 18.15 Was any other income/loss derived? Leads to repeating this structure.
}
export interface Schedule18ParticipationExemptionData {
  participations: ParticipationExemptionItem[];
  totalIncomeExempted: string; // 18.17 (Calculated)
  // 18.16 Did the TP derive any other income or losses from other Participation(s)? (Triggers more items)
  anyOtherIncomeLossFromOtherParticipations?: 'yes' | 'no' | '';
}

export interface PreGroupingNieItem { // For Tax Groups (19.5)
  id: string;
  memberName: string; // 19.5.1
  memberTrn: string; // 19.5.2
  broughtForward: string; // 19.5.3
  disallowedNieBroughtIntoGroupNewSub?: string; // 19.5.4
  usePreGroupingNie?: 'yes' | 'no' | ''; // 19.5.5
  taxableIncomeAttributable?: string; // 19.5.6
  taxLossAttributable?: string; // 19.5.7
  preGroupingNieUtilised?: string; // 19.5.8 (Calculated)
  preGroupingNieExpired?: string; // 19.5.9
  otherAdjustmentsIncreasePreGroupingNie?: string; // 19.5.10
  otherAdjustmentsDecreasePreGroupingNie?: string; // 19.5.11
  otherAdjustmentsDescPreGroupingNie?: string; // 19.5.12
  preGroupingNieCarriedForward?: string; // 19.5.13 (Calculated)
}
export interface Schedule19InterestCappingData {
  totalInterestExpenditureSubjectToGeneralRule: string; // 19.2.1
  totalInterestIncomeSubjectToGeneralRule: string; // 19.2.2
  disallowedNieBroughtForward: string; // 19.2.3
  // 19.2.4 is Net Interest Expenditure (Calculated)
  calculatedAdjustedEBITDA: string; // 19.2.5
  // 19.2.6 is Interest Deduction Limit (Calculated)
  // 19.2.7 is Deductible Net Interest Expenditure (Calculated)
  // 19.2.8 is Disallowed NIE for Current Period (Calculated)
  nieUtilisedFromBroughtForward: string; // 19.2.9 - This value is what's deducted in main form
  disallowedNieExpiredCurrentPeriod: string; // 19.3.4 (Single Entity) / 19.4.4 (Tax Group)
  otherAdjustmentsIncreaseDisallowedNie: string; // 19.3.5 / 19.4.5
  otherAdjustmentsDecreaseDisallowedNie: string; // 19.3.6 / 19.4.6
  otherAdjustmentsDesc: string; // 19.3.7 / 19.4.7
  // 19.3.8 / 19.4.8 is Disallowed NIE Carried Forward (Calculated)
  specificInterestNonDeductibleAmount: string; // From Main form (9.2.6)
  preGroupingNieItems: PreGroupingNieItem[];
  disallowedNieCarriedForward: string; // Calculated field (19.3.8 or 19.4.8)
  netInterestExpenditure: string; // Calculated (19.2.4)
  thirtyPercentEbitda: string; // Calculated
  interestDeductionLimit: string; // Calculated (19.2.6)
  deductibleNetInterestExpenditureCurrentPeriod: string; // Calculated (19.2.7)
  finalDisallowedNIEForCurrentPeriod: string; // Calculated (19.2.8)
}

export interface TaxReliefTransferItem { // For Sch 20
  id: string;
  counterpartyName: string; // 20.2.1
  counterpartyTrn: string; // 20.2.2
  transferorOrTransferee: 'transferor' | 'transferee' | ''; // 20.2.3
  proceeds: string; // 20.2.4
  netBookValue?: string; // 20.2.5 (Transferor only)
  dateOfTransfer: string; // 20.2.6
  gainOrLossInFS?: 'gain' | 'loss' | 'none' | ''; // 20.2.7 (Transferor only)
  gainInFS?: string; // 20.2.8 (Transferor, if gain)
  lossInFS?: string; // 20.2.9 (Transferor, if loss)
  // Fields for Clawback (20.3.x / 20.6.x)
  isTransferorStillTaxablePerson?: 'yes' | 'no' | ''; // 20.3.4 (QG)
  isTransferorNaturalPersonOrStillTaxable?: 'yes' | 'no' | ''; // 20.6.4 (BRR)
  marketValueAtOriginalTransfer?: string; // 20.3.5 / 20.6.5
  dateOfOriginalTransfer?: string; // 20.3.6 / 20.6.6
  gainRecognisedClawback?: string; // 20.3.7 / 20.6.7
  lossRecognisedClawback?: string; // 20.3.8 / 20.6.8
}
export interface Schedule20ReliefData {
  transfersWithinQualifyingGroupCurrentTaxPeriod: TaxReliefTransferItem[]; // Section 20.2
  transfersWithinQualifyingGroupPreviousTaxPeriodClawback: TaxReliefTransferItem[]; // Section 20.3
  businessRestructuringReliefCurrentTaxPeriod: TaxReliefTransferItem[]; // Section 20.5
  businessRestructuringReliefPreviousTaxPeriodClawback: TaxReliefTransferItem[]; // Section 20.6
  totalGainLossNotTaxedQGCurrentPeriod: string; // Calculated sum for main form
  totalGainLossRecognisedClawbackQG: string; // Calculated sum for main form
  totalGainLossNotTaxedBRRCurrentPeriod: string; // Calculated sum for main form
  totalGainLossRecognisedClawbackBRR: string; // Calculated sum for main form
}

export interface TransitionalRulePropertyItem { // For Sch 21.1 and 21.2
  id: string;
  address: string; // 21.1.1
  makaniNumber?: string; // 21.1.2
  dateOfOwnership: string; // 21.1.3
  originalCostOnCommencement: string; // 21.1.4
  netBookValueOnCommencement: string; // 21.1.5
  // Fields for disposed properties (Section 21.2)
  dateOfSale?: string; // 21.2.4
  transferredWithRelief?: 'yes' | 'no' | ''; // 21.2.7
  transactingPartyName?: string; // 21.2.8
  transactingPartyTrn?: string; // 21.2.9
  methodOfAdjustment?: 'valuation' | 'time_apportionment' | ''; // 21.2.10
  marketValueOnCommencement?: string; // 21.2.11 (if valuation)
  daysOwnedPriorCommencement?: string; // 21.2.12 (if time apportionment)
  totalDaysOwned?: string; // 21.2.13 (if time apportionment, calculated)
  proceedsOnDisposal?: string; // 21.2.14
  amountExcluded?: string; // 21.2.15 (Calculated)
}
export interface TransitionalRuleIntangibleItem { // For Sch 21.3
  id: string;
  description: string; // 21.3.1
  dateOfOwnership: string; // 21.3.2
  originalCostPriorCommencement: string; // 21.3.4
  netBookValuePriorCommencement: string; // 21.3.5
  // Fields for disposed items
  dateOfSale?: string; // 21.3.3
  transferredWithRelief?: 'yes' | 'no' | ''; // 21.3.6
  transactingPartyName?: string;
  transactingPartyTrn?: string;
  daysOwnedPriorCommencement?: string; // 21.3.9
  totalDaysOwned?: string; // 21.3.10 (Calculated)
  proceedsOnDisposal?: string; // 21.3.11
  amountExcluded?: string; // 21.3.12 (Calculated)
}
export interface TransitionalRuleFinancialItem { // For Sch 21.4 (Assets) and 21.5 (Liabilities)
  id: string;
  description: string; // 21.4.1 / 21.5.1
  dateOfOwnership: string; // 21.4.2 / 21.5.2
  netBookValueAtStart: string; // 21.4.5 / 21.5.5
  // Fields for disposed/settled items
  dateOfSale?: string; // Or settlement date (21.4.3 / 21.5.3)
  exemptUnderParticipation?: 'yes' | 'no' | ''; // Specific to assets (21.4.4)
  transferredWithRelief?: 'yes' | 'no' | ''; // 21.4.6 / 21.5.6
  transactingPartyName?: string;
  transactingPartyTrn?: string;
  marketValueAtStart?: string; // 21.4.9 / 21.5.9
  proceedsOnDisposal?: string; // Or settlement value (21.4.10 / 21.5.10)
  amountExcluded?: string; // 21.4.11 / 21.5.11 (Calculated)
}
export interface Schedule21TransitionalRulesData {
  qualifyingImmovablePropertiesSubjectToElection: TransitionalRulePropertyItem[];
  qualifyingImmovablePropertiesDisposed: TransitionalRulePropertyItem[];
  qualifyingIntangibleAssets: TransitionalRuleIntangibleItem[];
  qualifyingFinancialAssets: TransitionalRuleFinancialItem[];
  qualifyingFinancialLiabilities: TransitionalRuleFinancialItem[];
  totalAmountExcludedImmovableProperty: string; // Calculated
  totalAmountExcludedIntangibleAssets: string; // Calculated
  totalAmountExcludedFinancialAssets: string; // Calculated
  totalAmountExcludedFinancialLiabilities: string; // Calculated
}

export interface OtherScheduleIncomeLossItem { // For Sch 22.1
  id: string;
  description: string; // 22.1.1
  isUnrealised: 'yes' | 'no' | ''; // 22.1.2
  // hasTaxablePersonElectedRealisationBasis: 'yes' | 'no' | ''; // 22.1.3 (This is global, not per item)
  originalCost?: string; // 22.1.4
  netBookValueEndOfPeriod?: string; // 22.1.5
  incomeRecognisedNotInIS?: string; // 22.1.6
  lossRecognisedNotInIS?: string; // 22.1.7
}
export interface OtherScheduleUnrealisedGainLossItem { // For Sch 22.2
  id: string;
  description: string; // 22.2.1
  originalCost: string; // 22.2.2
  netBookValueEndOfPeriod: string; // 22.2.3
  unrealisedGainRecognised?: string; // 22.2.4
  unrealisedLossRecognised?: string; // 22.2.5
}
export interface OtherScheduleDeferredGainLossItem { // For Sch 22.3
  id: string;
  description: string; // 22.3.1
  dateRealisationEvent: string; // 22.3.2
  taxPeriodDeferred: string; // 22.3.3
  previouslyRecognisedInIS?: 'yes' | 'no' | ''; // 22.3.4
  gainRealised?: string; // 22.3.5
  lossRealised?: string; // 22.3.6
}
export interface Schedule22OtherSubScheduleData {
  incomeLossesNotInIS: OtherScheduleIncomeLossItem[];
  unrealisedGainsLosses: OtherScheduleUnrealisedGainLossItem[];
  deferredGainsLosses: OtherScheduleDeferredGainLossItem[];
  totalIncomeAdjustmentNotInIS: string; // 22.1.8 (Calculated)
  totalLossAdjustmentNotInIS: string; // 22.1.9 (Calculated)
  totalUnrealisedGainsExcluded: string; // Calculated for main form
  totalUnrealisedLossesExcluded: string; // Calculated for main form
  totalDeferredGainsRealised: string; // Calculated for main form
  totalDeferredLossesRealised: string; // Calculated for main form
}

export interface AttachmentItem {
  id: string;
  documentKey: string; // e.g., 'financial_statements', 'tp_documentation'
  documentName: string; // Translated name, e.g., "Financial Statements"
  isMandatory: boolean;
  condition?: (formData: FormData) => boolean;
  file?: File;
  fileName?: string;
  reasonIfNotProvided?: string;
}
export interface Schedule22_4_AttachmentsData {
  attachments: AttachmentItem[];
}


export interface ReviewDetails { // Corresponds to CTGTXR1 Section 11
  declarationConfirmed: boolean; // 11.4 implicitly
  signedBy: string; // 11.5
  capacity: string; // 11.6
  submissionDate: string; // 11.1
}

export interface FormData {
  taxablePersonDetails: TaxablePersonDetails;
  elections: Elections;
  accounting: AccountingSchedule;
  accountingAdjustments: AccountingAdjustments;
  relief: ReliefApplications;
  otherAdjustments: OtherAdjustments;
  taxLiability: TaxLiability;
  freeZoneSchedule: FreeZoneScheduleData;
  review: ReviewDetails;
  schedule12IP: Schedule12IPData;
  schedule13UaeDividends: Schedule13UaeDividendsData;
  schedule14ForeignPE: Schedule14ForeignPEData;
  schedule15TaxCredits: Schedule15TaxCreditData;
  schedule16RelatedParty: Schedule16RelatedPartyData;
  schedule16ConnectedPersons: Schedule16ConnectedPersonsData;
  schedule17TaxLosses: Schedule17TaxLossData;
  schedule17TaxGroupLosses: Schedule17TaxGroupLossData;
  schedule18ParticipationExemption: Schedule18ParticipationExemptionData;
  schedule19InterestCapping: Schedule19InterestCappingData;
  schedule20Relief: Schedule20ReliefData;
  schedule21TransitionalRules: Schedule21TransitionalRulesData;
  schedule22Other: Schedule22OtherSubScheduleData;
  schedule22_4_Attachments: Schedule22_4_AttachmentsData;
}

export const initialFormData: FormData = {
  taxablePersonDetails: {
    filingAs: '',
    isExemptPerson: '',
    exemptPersonHasTaxableBusiness: '',
    taxablePersonName: '',
    trn: '',
    primaryBusinessActivity: '',
    mainAddress: { addressLine1: '', city: '', emirateOrState: '', country: 'AE' },
    taxPeriodStartDate: '',
    taxPeriodEndDate: '',
    contactPerson: { contactName: '', contactEmail: '', contactMobile: '' },
    isUboInfoDisclosed: '',
    uboDetails: [{id: 'initial-ubo', uboName: '', uboTrnTaxId: '', uboNationality: '', uboCountryOfResidence: ''}],
    naturalPersonSpecifics: {
        uaeResidencyStatus: '', nationality: '', turnover: '', accountingBasis: '',
        conductsMultipleBusinesses: '', businessActivitiesCount: '1',
        businessActivityList: [{id: 'np-initial-activity', activity:''}],
        revenuePercentageBySector: [{id: 'np-initial-sector', sector: '', percentage: ''}],
        taxResidencyStatusDTA: '', nonResidentCountryDTA: '',
        isPartnerInUnincorporatedPartnerships: '', unincorporatedPartnerships: [{id:'np-initial-up', name:'', trn:''}],
    },
    juridicalPersonSpecifics: {
        incorporationDate: '', incorporationCountry: '', isUaeOrFzIncorporated: '',
        isTaxResidentInForeignJurisdictionUnderDTA: '', dtaForeignJurisdictionCountry: '', isNonResidentInUaeUnderDTA: '',
        isForeignIncorporatedUaeControlled: '', nonResidentFilingBasis: '',
        isBankOrInsuranceProvider: '', isMneGroupMember: '', revenueForTaxPeriod: '', accountingBasis: '',
        conductsMultipleBusinesses: '', businessActivitiesCount: '1',
        businessActivityList: [{id: 'jp-initial-activity', activity:''}],
        revenuePercentageBySector: [{id: 'jp-initial-sector', sector: '', percentage: ''}],
        isPartnerInUnincorporatedPartnerships: '', unincorporatedPartnerships: [{id:'jp-initial-up', name:'', trn:''}],
        isGovernmentEntityOrAffiliate: '', conductsTaxableActivityIfGovt: '',
        isFreeZonePerson: '', electionToForego0RateFZ: '', meetsQualifyingFZRequirements: '',
    },
    unincorporatedPartnershipSpecifics: {
        isMneGroupMember: '', isPartnerInOtherUPs: '', otherUPs: [{id:'up-initial-other-up', name:'', trn:''}],
        revenueForTaxPeriod: '', accountingBasis: '', conductsMultipleBusinesses: '', businessActivitiesCount: '1',
        businessActivityList: [{id: 'up-initial-activity', activity:''}],
        revenuePercentageBySector: [{id: 'up-initial-sector', sector: '', percentage: ''}],
    },
    taxGroupSpecifics: {
        taxGroupName: '', parentCompanyTrn: '', members: [{id: 'tg-initial-member', trn: '', name: ''}],
        isMneGroupMember: '', isPartnerInUnincorporatedPartnerships: '',
        unincorporatedPartnerships: [{id:'tg-initial-up', name:'', trn:''}],
        consolidatedRevenue: '', accountingBasis: '', conductsMultipleBusinesses: '', businessActivitiesCount: '1',
        businessActivityList: [{id: 'tg-initial-activity', activity:''}],
        revenuePercentageBySector: [{id: 'tg-initial-sector', sector: '', percentage: ''}],
    },
  },
  elections: {
    realisationBasis: false,
    realisationBasisBankOption: '',
    transitionalRulesImmovableProperty: false,
    transitionalRulesIntangibleAssets: false,
    transitionalRulesFinancialAssetsLiabilities: false,
    smallBusinessRelief: false,
    hasTransfersWithinQGInPeriod: false,
    transfersWithinQualifyingGroup: false,
    hasBusinessRestructuringInPeriod: false,
    businessRestructuringRelief: false,
    hasForeignPEs: false,
    foreignPermanentEstablishmentExemption: false,
  },
  accounting: {
    accountingIncome: '',
    financialStatementsAudited: '',
    auditOpinion: '',
    auditorName: '',
    incomeStatement_revenue: '',
    incomeStatement_costOfSales: '',
    incomeStatement_grossProfit: '',
    incomeStatement_operatingExpenses: '',
    incomeStatement_otherIncome: '',
    oci_itemsNetOfTax: '',
    balanceSheet_totalAssets: '',
    balanceSheet_totalLiabilities: '',
    balanceSheet_totalEquity: '',
  },
  accountingAdjustments: {
    equityMethodInvestmentAccounting: '', equityMethodShareOfProfits: '', equityMethodShareOfLosses: '',
    unincorporatedPartnershipSeparateTP: '', unincorporatedPartnershipNetProfits: '', unincorporatedPartnershipNetLosses: '',
    upInterestDisposalGainsPE: '', upInterestDisposalLossesPE: '',
    incomeLossesNotInISRecognised: '', gainsNotInIS: '0.00', lossesNotInIS: '0.00',
    unrealisedGainsRecognisedCurrentTaxPeriod: '0.00', unrealisedLossesRecognisedCurrentTaxPeriod: '0.00',
    deferredGainsRealisedCurrentTaxPeriod: '0.00', deferredLossesRealisedCurrentTaxPeriod: '0.00',
    depreciationAdjustmentsRealisationDecrease: '', depreciationAdjustmentsRealisationIncrease: '',
    otherRealisationAdjustmentsDecreaseTI: '', otherRealisationAdjustmentsIncreaseTI: '', otherRealisationAdjustmentsDesc: '',
    gainsOnElectedImmovableProperty: '0.00', transitionalRulesIntangibleAssetsAdjustment: '0.00', transitionalRulesFinancialAssetsLiabilitiesAdjustment: '0.00',
    dividendsFromUaeResident: '', totalDividendsFromUaeResident: '0.00',
    incomeLossesFromParticipatingInterest: '', incomeFromParticipatingInterestExempted: '0.00', incomeFromParticipatingInterestClawback: '', lossesFromParticipatingInterestClawback: '',
    taxableIncomeLossFromEligibleForeignPE: '0.00',
    incomeFromInternationalAircraftShippingNonResident: '', countryInternationalAircraftShipping: '', incomeDerivedIntlAircraftShipping: '', expenditureIntlAircraftShipping: '',
  },
  relief: {
    transfersWithinQualifyingGroupAdjustmentNeeded: '',
    gainsLossesSubjectToTgReliefCurrentPeriod: '0.00', gainsLossesFromTgClawback: '0.00',
    qgExcludedUnrealisedGainsTransferee: '', qgExcludedUnrealisedLossesTransferee: '',
    qgRealisedGainsPreviouslyExcluded: '', qgRealisedLossesPreviouslyExcluded: '',
    qgDepreciationAdjustmentDecreaseTransferee: '', qgDepreciationAdjustmentIncreaseTransferee: '',
    qgOtherAdjustmentsClawbackIncreaseTI: '', qgOtherAdjustmentsClawbackDecreaseTI: '', qgOtherAdjustmentsDesc: '',
    totalGainLossNotTaxedQGCurrentPeriod: '0.00', totalGainLossRecognisedClawbackQG: '0.00',
    businessRestructuringReliefAdjustmentNeeded: '',
    gainsLossesSubjectToBrrReliefCurrentPeriod: '0.00', gainsLossesFromBrrClawback: '0.00',
    brrExcludedUnrealisedGainsTransferee: '', brrExcludedUnrealisedLossesTransferee: '',
    brrRealisedGainsPreviouslyExcluded: '', brrRealisedLossesPreviouslyExcluded: '',
    brrDepreciationAdjustmentDecreaseTransferee: '', brrDepreciationAdjustmentIncreaseTransferee: '',
    brrOtherAdjustmentsClawbackIncreaseTI: '', brrOtherAdjustmentsClawbackDecreaseTI: '', brrOtherAdjustmentsDesc: '',
    totalGainLossNotTaxedBRRCurrentPeriod: '0.00', totalGainLossRecognisedClawbackBRR: '0.00',
  },
  otherAdjustments: {
    nonDeductibleEntertainmentExp: '', nonDeductiblePensionContributions: '', nonDeductibleCharitableDonationsNonQPBE: '',
    expenditureDerivingExemptIncomeNonInterest: '', ownerDividendsExpensed: '', naturalPersonWithdrawals: '',
    expensesNotWhollyExclusivelyBusiness: '', otherNonDeductibleExpenditure: '', otherNonDeductibleExpenditureDesc: '',
    netInterestExpExceedsThreshold: '', disallowedNetInterestExpCurrentPeriod: '0.00', broughtForwardNieUtilised: '0.00',
    preGroupingNIETaxGroups: '', specificInterestNonDeductible: '',
    relatedPartyTransactionsExist: '', relatedPartyTransactionsAggregateValueExceeds40M: '', additionsArmslengthAdjustments: '0.00',
    deductionsArmslengthAdjustments: '',
    relatedPartyAssetGainLossRealised: '', relatedPartyAssetGain: '', relatedPartyAssetLoss: '',
    connectedPersonsTransactionsExist: '', connectedPersonsPaymentsAggregateValueExceeds500K: '', adjustmentsConnectedPersonsNonMarketValue: '0.00',
    investorInQIF: '', qifNetInterestIncome: '', qifNetRentalIncome: '', qifNetExemptIncome: '', qifOtherIncome: '', qifDividendsDeclaredByTPFromQIF: '',
    priorPeriodErrorCorrectionImpactAED10000OrLess: '', priorPeriodErrorAdjustmentsTaxPeriods: [], priorPeriodErrorAdjustmentsIncreaseTI: '',
    priorPeriodErrorAdjustmentsDecreaseTI: '', priorPeriodErrorAdjustmentsDesc: '', otherAdjustmentsNotCapturedAbove: '',
    otherAdjustmentsNotCapturedIncreaseTI: '', otherAdjustmentsNotCapturedDecreaseTI: '', otherAdjustmentsNotCapturedDesc: '',
  },
  taxLiability: {
    taxableIncomeLossBeforeTaxLossUtilisation: '0.00', hasBroughtForwardTaxLosses: '',
    claimOrSurrenderTaxLossesGroupEntity: '', taxLossesUtilisedCurrentPeriod: '0.00',
    taxLossesClaimedFromOtherGroupEntities: '', preGroupingTaxLossesUtilisedTaxGroup: '', taxLossesClaimedFromOtherGroupEntitiesTaxGroup: '',
    taxableIncomeAfterTaxLossAdjustments: '0.00', corporateTaxLiabilityBeforeCredits: '0.00',
    wishToUseTaxCredits: '', taxCreditsApplied: '0.00', corporateTaxPayable: '0.00',
    estimatedFiguresIncludedInReturn: '', estimatedFiguresDetails: '',
  },
  review: {
    declarationConfirmed: false, signedBy: '', capacity: '', submissionDate: new Date().toISOString().split('T')[0],
  },
  freeZoneSchedule: {
    totalRevenueFinancialStatements: '', hasDomesticOrForeignPE: '', revenueAttrToPE: '',
    hasRevenueFromNonFzCommercialPropertyInFz: '', revenueNonFzCommercialPropertyInFz: '',
    hasRevenueFromNonCommercialPropertyInFz: '', revenueNonCommercialPropertyInFz: '',
    hasRevenueFromIp: '', revenueIpNonQualifying: '0.00', totalRevenueForDeMinimis: '0.00',
    revenueExcludedActivities: '', revenueNonQualifyingActivitiesNonFzp: '', revenueTransactionsNonBeneficialRecipient: '',
    totalNonQualifyingRevenue: '0.00', deMinimisMetResult: true,
    revenueFromFzpQualifying: '', revenueFromNonFzpQualifyingActivities: '',
    qualifyingActivities: { manufacturing: false, processing: false, holdingShares: false, shipManagement: false, reinsurance: false, fundManagement: false, wealthManagement: false, headquarters: false, treasury: false, aircraftFinancing: false, distributionDesignatedZone: false, logistics: false, ancillaryActivities: false },
    revenueIpQualifying: '0.00', allEmployeesInFz: '', avgNumFullTimeEmployees: '', avgNumFullTimeEmployeesInFz: '',
    totalOperatingExpenditureQI: '', totalCapitalExpenditureQI: '', coreIncomeActivitiesOutsourced: '',
    numOutsourcingProviders: '0', outsourcingProviders: [],
    adequateAssetsInFz: false, adequateEmployeesInFz: false, adequateOperatingExpenditureInFz: false,
    relatedPartyTransactionsArmslength: false, transferPricingDocumentationPrepared: false, auditedFinancialStatementsPrepared: false,
    emirateBreakdown: [{ id: 'initial-emirate', emirate: '', ebitda: '', salariesAndWages: '' }], additionalInformation: '',
    calculatedQualifyingIncome: '0.00', calculatedNonQualifyingIncome: '0.00',
  },
  schedule12IP: { ipItems: [{id: 's12ip-initial', description: '', overallIncome:'', overallExpenditure:'', qualifyingExpenditure:''}], totalIncomeFromOtherIPNonQualifying: '', totalIncomeInExcessOfQI: '0.00', totalRevenueFromIPNotQualifyingIncome: '0.00', totalRevenueFromQualifyingIP: '0.00'},
  schedule13UaeDividends: { items: [{id:'s13div-initial', companyName:'', trn:'', amount:''}], totalDividendsReceived: '0.00' },
  schedule14ForeignPE: {
    priorPeriods: [{id:'s14fp-initial-pp', taxPeriodIdentifier:'', taxableIncomeFPE:'', taxLossFPE:'', electionMadeInPeriod:'', lossesUtilisedAgainstMainTI:'', lossesUtilisedAgainstFPEIncome:'', cfLossesUtilisedAgainstFPEIncome: '0.00'}],
    currentFPEligibleNetIncome: '', currentFPEligibleNetLoss: '',
    ineligiblePEs: [{id:'s14fp-initial-ineligible', jurisdictionName:''}],
    netIncomeLossForAdjustment: '0.00', priorLossesFullyOffset: true
  },
  schedule15TaxCredits: {
    foreignTaxCredits: [{id:'s15ftc-initial', country:'', incomeSubjectToForeignTax:'', foreignTaxPaid:'', evidenceConfirmation:'', natureOfCredit:''}],
    foreignTaxCreditsForGroupMembers: [{id:'s15ftcgroup-initial', memberName:'', taxableIncomeAttributableToMember:'', country:'', incomeSubjectToForeignTax:'', foreignTaxPaid:'', evidenceConfirmation:'', natureOfCredit:''}],
    totalTaxCreditsClaimed: '0.00'
  },
  schedule16RelatedParty: {
    transactions: [{id:'s16rp-initial', name:'', transactionType:'', taxResidence:'', trnOrTin:'', grossIncomeOrExpense:'', transferPricingMethod:'', armsLengthValue:''}],
    totalUpwardAdjustments: '0.00', totalDownwardAdjustments: '0.00'
  },
  schedule16ConnectedPersons: {
    payments: [{id:'s16cp-initial', name:'', trnOrTin:'', paymentOrBenefitType:'', description:'', valueProvidedByTP:'', marketValue:''}],
    totalAdjustments: '0.00'
  },
  schedule17TaxLosses: {
    broughtForwardLosses: [{id:'s17tl-initial-bf', description:'', amount:''}],
    taxLossIncurredCurrentPeriod: '0.00',
    taxLossesReceivedBizRestructuring: [{id:'s17tl-initial-br', description:'', amount:''}],
    taxLossesTransferredBizRestructuring: '',
    taxLossesLimitedChangeOwnership: '', otherAdjustmentsIncrease: '', otherAdjustmentsDecrease: '', otherAdjustmentsDesc: '',
    taxLossesReceivedFromOthers: [{id:'s17tl-initial-ro', entityName:'', trn:'', amount:''}],
    taxLossesTransferredToOthers: [{id:'s17tl-initial-to', entityName:'', trn:'', amount:''}],
    taxLossesUtilisedInCurrentPeriodFromSchedule: '0.00',
    taxLossesCarriedForwardAvailableForTransfer: '0.00',
    taxLossesCarriedForwardFinal: '0.00'
  },
  schedule17TaxGroupLosses: {
    newSubsidiaries: [{id:'s17tgl-initial-ns', name:'', trn:'', preGroupingLossBroughtIn:'', taxableIncomeAttributable:''}],
    preGroupingLossItems: [{id:'s17tgl-initial-pgl', subsidiaryName:'', subsidiaryTrn:'', preGroupingLossBroughtForward:'', taxableIncomeAttributable:'', lossAttributable:''}],
    taxGroupLossesBroughtForward: '', taxGroupLossIncurredCurrentPeriod: '0.00',
    taxGroupLossesReceivedBizRestructuring: '', taxGroupLossesTransferredBizRestructuring: '',
    taxGroupLossesLimitedChangeOwnership: '',
    taxLossesClaimedFromOthers: [{id:'s17tgl-initial-ro', entityName:'', trn:'', amount:''}],
    taxLossesTransferredToOthers: [{id:'s17tgl-initial-to', entityName:'', trn:'', amount:''}],
    finalTaxGroupLossesCarriedForward: '0.00',
    taxGroupLossesUtilisedInCurrentPeriod: '0.00',
    taxGroupLossesCarriedForwardAvailableForTransfer: '0.00',
    taxGroupOtherAdjustmentsIncrease: '', taxGroupOtherAdjustmentsDecrease:'', taxGroupOtherAdjustmentsDesc:'',
  },
  schedule18ParticipationExemption: {
    participations: [{id:'s18pe-initial', name:'', countryOfResidency:'', trnOrTin:'', subjectToTaxConfirmation:'', subjectToTaxReason:'', incomeLossItems:[{id:'s18pe-initial-il', natureOfIncomeLoss:'', incomeToExempt:'', lossToExempt:''}], ownershipPercentage:'', acquisitionCost:'', dateIncomeLossDerived:'', heldFor12MonthsConfirmation:'', intendToHoldFor12MonthsConfirmation: '', otherConditionsMetConfirmation:''}],
    totalIncomeExempted: '0.00',
    anyOtherIncomeLossFromOtherParticipations: ''
  },
  schedule19InterestCapping: {
    totalInterestExpenditureSubjectToGeneralRule: '', totalInterestIncomeSubjectToGeneralRule: '',
    disallowedNieBroughtForward: '', calculatedAdjustedEBITDA: '', nieUtilisedFromBroughtForward: '0.00',
    disallowedNieExpiredCurrentPeriod: '', otherAdjustmentsIncreaseDisallowedNie: '', otherAdjustmentsDecreaseDisallowedNie: '',
    otherAdjustmentsDesc: '', specificInterestNonDeductibleAmount: '',
    preGroupingNieItems: [{id:'s19ic-initial-pg', memberName:'', memberTrn:'', broughtForward:'', disallowedNieBroughtIntoGroupNewSub:'', usePreGroupingNie:'', taxableIncomeAttributable:'', taxLossAttributable:'', preGroupingNieUtilised:'', preGroupingNieExpired:'', otherAdjustmentsIncreasePreGroupingNie:'', otherAdjustmentsDecreasePreGroupingNie:'', otherAdjustmentsDescPreGroupingNie:'', preGroupingNieCarriedForward:''}],
    disallowedNieCarriedForward: '0.00',
    netInterestExpenditure: '0.00', thirtyPercentEbitda: '0.00', interestDeductionLimit: '12000000.00',
    deductibleNetInterestExpenditureCurrentPeriod: '0.00', finalDisallowedNIEForCurrentPeriod: '0.00'
  },
  schedule20Relief: {
    transfersWithinQualifyingGroupCurrentTaxPeriod: [{id:'s20qgcurrent-initial', counterpartyName:'', counterpartyTrn:'', transferorOrTransferee:'', proceeds:'', dateOfTransfer:''}],
    transfersWithinQualifyingGroupPreviousTaxPeriodClawback: [{id:'s20qgclawback-initial', counterpartyName:'', counterpartyTrn:'', transferorOrTransferee:'', proceeds:'', dateOfTransfer:''}],
    businessRestructuringReliefCurrentTaxPeriod: [{id:'s20brrcurrent-initial', counterpartyName:'', counterpartyTrn:'', transferorOrTransferee:'', proceeds:'', dateOfTransfer:''}],
    businessRestructuringReliefPreviousTaxPeriodClawback: [{id:'s20brrclawback-initial', counterpartyName:'', counterpartyTrn:'', transferorOrTransferee:'', proceeds:'', dateOfTransfer:''}],
    totalGainLossNotTaxedQGCurrentPeriod: '0.00', totalGainLossRecognisedClawbackQG: '0.00',
    totalGainLossNotTaxedBRRCurrentPeriod: '0.00', totalGainLossRecognisedClawbackBRR: '0.00'
  },
  schedule21TransitionalRules: {
    qualifyingImmovablePropertiesSubjectToElection: [{id:'s21prop-initial', address:'', dateOfOwnership:'', originalCostOnCommencement:'', netBookValueOnCommencement:''}],
    qualifyingImmovablePropertiesDisposed: [{id:'s21propdisp-initial', address:'', dateOfOwnership:'', originalCostOnCommencement:'', netBookValueOnCommencement:''}],
    qualifyingIntangibleAssets: [{id:'s21int-initial', description:'', dateOfOwnership:'', originalCostPriorCommencement:'', netBookValuePriorCommencement:''}],
    qualifyingFinancialAssets: [{id:'s21fa-initial', description:'', dateOfOwnership:'', netBookValueAtStart:''}],
    qualifyingFinancialLiabilities: [{id:'s21fl-initial', description:'', dateOfOwnership:'', netBookValueAtStart:''}],
    totalAmountExcludedImmovableProperty: '0.00', totalAmountExcludedIntangibleAssets: '0.00',
    totalAmountExcludedFinancialAssets: '0.00', totalAmountExcludedFinancialLiabilities: '0.00'
  },
  schedule22Other: {
    incomeLossesNotInIS: [{id:'s22other-initial-is', description:'', isUnrealised:''}],
    unrealisedGainsLosses: [{id:'s22other-initial-ugl', description:'', originalCost:'', netBookValueEndOfPeriod:''}],
    deferredGainsLosses: [{id:'s22other-initial-dgl', description:'', dateRealisationEvent:'', taxPeriodDeferred:''}],
    totalIncomeAdjustmentNotInIS: '0.00', totalLossAdjustmentNotInIS: '0.00',
    totalUnrealisedGainsExcluded: '0.00', totalUnrealisedLossesExcluded: '0.00',
    totalDeferredGainsRealised: '0.00', totalDeferredLossesRealised: '0.00'
  },
  schedule22_4_Attachments: {
    attachments: [
      { id: 'att1', documentKey: 'financial_statements', documentName: '', isMandatory: true, condition: (formData: FormData) => !formData.elections.smallBusinessRelief },
      { id: 'att2', documentKey: 'market_value_docs_transitional_rules', documentName: '', isMandatory: false, condition: (formData: FormData) => formData.elections.transitionalRulesImmovableProperty || formData.elections.transitionalRulesFinancialAssetsLiabilities || formData.elections.transitionalRulesIntangibleAssets },
      { id: 'att3', documentKey: 'tax_residency_certificate_foreign', documentName: '', isMandatory: false, condition: (formData: FormData) => (formData.taxablePersonDetails.juridicalPersonSpecifics?.isTaxResidentInForeignJurisdictionUnderDTA === 'yes' || formData.taxablePersonDetails.naturalPersonSpecifics?.taxResidencyStatusDTA === 'non-resident') },
      { id: 'att4', documentKey: 'foreign_tax_evidence_ftc', documentName: '', isMandatory: false, condition: (formData: FormData) => formData.taxLiability.wishToUseTaxCredits === 'yes' && (formData.schedule15TaxCredits.foreignTaxCredits.some(ftc => ftc.evidenceConfirmation === 'yes') || formData.schedule15TaxCredits.foreignTaxCreditsForGroupMembers.some(ftc => ftc.evidenceConfirmation === 'yes') )},
      { id: 'att5', documentKey: 'tp_documentation', documentName: '', isMandatory: false, condition: (formData: FormData) => (formData.taxablePersonDetails.juridicalPersonSpecifics?.isMneGroupMember === 'yes' || formData.otherAdjustments.relatedPartyTransactionsAggregateValueExceeds40M === 'yes') },
      { id: 'att6', documentKey: 'qfzp_supporting_docs', documentName: '', isMandatory: false, condition: (formData: FormData) => ((formData.taxablePersonDetails.filingAs === 'free_zone_person' || formData.taxablePersonDetails.juridicalPersonSpecifics?.isFreeZonePerson === 'yes') && formData.taxablePersonDetails.juridicalPersonSpecifics?.meetsQualifyingFZRequirements === 'yes' && formData.taxablePersonDetails.juridicalPersonSpecifics?.electionToForego0RateFZ === 'no') },
      { id: 'att7', documentKey: 'other_docs', documentName: '', isMandatory: false },
    ],
  }
};

export type Step = {
  name: string;
  component: React.FC<StepProps>;
  validation: (data: FormData, t: TranslationFunction) => FormErrorsStep;
  errorKey: keyof FormData | ScheduleName;
};

export interface FormErrorsStep {
  [key: string]: string | FormErrorsStep | FormErrorsStep[] | undefined;
}

export type FormErrors = {
  [K in keyof FormData | ScheduleName]?: FormErrorsStep;
};


export type ScheduleName =
  | 'freeZoneSchedule'
  | 'schedule12IP'
  | 'schedule13UaeDividends'
  | 'schedule14ForeignPE'
  | 'schedule15TaxCredits'
  | 'schedule16RelatedParty'
  | 'schedule16ConnectedPersons'
  | 'schedule17TaxLosses'
  | 'schedule17TaxGroupLosses'
  | 'schedule18ParticipationExemption'
  | 'schedule19InterestCapping'
  | 'schedule20Relief'
  | 'schedule21TransitionalRules'
  | 'schedule22Other'
  | 'schedule22_4_Attachments';


export type ScheduleDataTypes =
  | FreeZoneScheduleData
  | Schedule12IPData
  | Schedule13UaeDividendsData
  | Schedule14ForeignPEData
  | Schedule15TaxCreditData
  | Schedule16RelatedPartyData
  | Schedule16ConnectedPersonsData
  | Schedule17TaxLossData
  | Schedule17TaxGroupLossData
  | Schedule18ParticipationExemptionData
  | Schedule19InterestCappingData
  | Schedule20ReliefData
  | Schedule21TransitionalRulesData
  | Schedule22OtherSubScheduleData
  | Schedule22_4_AttachmentsData;

export type CurrentScheduleInfo = {
  name: ScheduleName;
  data: ScheduleDataTypes;
} | null;


export interface StepProps {
  formData: FormData;
  onChange: (section: keyof FormData | ScheduleName, field: string, value: any) => void;
  errors: FormErrorsStep;
  t: TranslationFunction;
  openScheduleModal?: (scheduleName: ScheduleName, scheduleData: ScheduleDataTypes) => void;
  setFormData?: React.Dispatch<React.SetStateAction<FormData>>;
}

export interface GenericScheduleProps<T extends ScheduleDataTypes> {
  scheduleData: T;
  onChange: (field: keyof T | string, value: any, index?: number, subField?: keyof any, itemIndex?: number, itemSubField?: keyof any) => void;
  errors: FormErrorsStep;
  t: TranslationFunction;
  fullFormData: FormData;
}
